// Full system-of-record fixtures for the Explainability drill-through drawer.
// Each record is what a buyer would see if they clicked into their live ERP/QMS.
// Read-only, deterministic, mono-formatted.

export const emailRecord = {
  key: "email",
  system: "Microsoft 365 · shared mailbox",
  recordId: "meridian-suppliers · thread 20260514-08:41",
  fields: [
    { label: "From", value: "production@apex-precision.example" },
    { label: "To", value: "buyers@meridian.example" },
    { label: "Cc", value: "quality@meridian.example, m.chen@meridian.example" },
    { label: "Subject", value: "Weekly production update · Apex · wk20" },
    { label: "Received", value: "May 14, 2026 · 08:41 CDT" },
    { label: "Attachments", value: "none" },
  ],
  body: [
    {
      kind: "paragraph",
      text: "Team — quick roundup on this week's lots. Deburr throughput held steady at 96%, and the CMM queue is on plan. Two supplier POs at Meridian are on-schedule for the promised windows.",
    },
    {
      kind: "paragraph",
      text: "PO 4468 (cortical screw shafts) is packed and dispatched Thursday. PO 4470 (bone anchors) will ship on the promised May 22.",
    },
    {
      kind: "highlight",
      text: "Also flagging that the anodize rework queue pushes lot 4471-B roughly two weeks past the original commit, so we are targeting the week of June 8 for release.",
    },
    {
      kind: "paragraph",
      text: "We will send updated tracking once outbound is booked. Let us know if you need a status call before Friday.",
    },
    {
      kind: "signature",
      text: "— Ella Vance · Production Coordinator · Apex Precision Machining",
    },
  ],
};

export const poRecord = {
  key: "po",
  system: "SAP · MM module",
  recordId: "PO 4471",
  fields: [
    { label: "Purchase order", value: "PO 4471", mono: true },
    { label: "Supplier", value: "Apex Precision Machining" },
    { label: "Buyer", value: "M. Chen" },
    { label: "Created", value: "March 12, 2026", mono: true },
    { label: "Currency", value: "USD" },
    { label: "Incoterms", value: "FOB Warsaw, IN" },
    { label: "Payment terms", value: "Net 30" },
    { label: "Status", value: "Open · revised" },
  ],
  lineItems: [
    {
      line: "10",
      partNumber: "APX-TLP-2140",
      description: "Titanium locking plate · rev D · lot 4471-B",
      qty: "240 ea",
      unitPrice: "$482.00",
      promised: "June 12, 2026",
    },
    {
      line: "20",
      partNumber: "APX-TLP-2141",
      description: "Titanium locking plate · rev D · lot 4471-A",
      qty: "180 ea",
      unitPrice: "$482.00",
      promised: "May 26, 2026",
    },
  ],
  revisions: [
    {
      rev: "0",
      date: "March 12, 2026",
      by: "M. Chen",
      change: "PO created · both lots promised for May 29, 2026.",
    },
    {
      rev: "1",
      date: "April 03, 2026",
      by: "SAP · EDI",
      change: "Apex order confirmation received · dates confirmed.",
    },
    {
      rev: "2",
      date: "May 14, 2026",
      by: "SYSTEM · risk radar",
      change:
        "Slip signal from Apex correspondence · lot 4471-B revised to June 12, 2026 · lot 4471-A held at May 26.",
    },
  ],
};

export const woRecord = {
  key: "wo",
  system: "MES · production execution",
  recordId: "WO 8812",
  fields: [
    { label: "Work order", value: "WO 8812", mono: true },
    { label: "Product", value: "Locking plate assembly · MSS-LP rev D" },
    { label: "Quantity", value: "240 ea", mono: true },
    { label: "Line", value: "Warsaw, IN · Line 1" },
    { label: "Scheduled start", value: "June 1, 2026", mono: true },
    { label: "Scheduled end", value: "June 3, 2026", mono: true },
    { label: "Status", value: "Blocked · awaiting material" },
    { label: "Depends on", value: "PO 4471 · lot 4471-B", mono: true },
  ],
  routing: [
    { op: "10", name: "Material receipt · lot verification", station: "Receiving dock A" },
    { op: "20", name: "Cleaning · ultrasonic", station: "WS-04" },
    { op: "30", name: "Assembly · plate + fasteners", station: "WS-08" },
    { op: "40", name: "Torque check · sample n=8", station: "QC-02" },
    { op: "50", name: "Serialization · UDI print", station: "WS-11" },
    { op: "60", name: "Final pack + shipper label", station: "Pack-A" },
  ],
  materials: [
    {
      component: "Titanium locking plate · lot 4471-B",
      qty: "240 ea",
      status: "Blocked · PO 4471 revised",
    },
    { component: "Cortical screw · rev C · lot CST-2201", qty: "1,920 ea", status: "On hand" },
    { component: "UDI label roll · UL-2026-05", qty: "1 roll", status: "On hand" },
  ],
};

export const soRecord = {
  key: "so",
  system: "SAP · SD module",
  recordId: "SO 30117",
  fields: [
    { label: "Sales order", value: "SO 30117", mono: true },
    { label: "Customer", value: "Northlake Health" },
    { label: "Customer PO", value: "NLH-PO-88214", mono: true },
    { label: "Salesperson", value: "T. Volkov" },
    { label: "Order value", value: "$840,000", mono: true },
    { label: "Committed delivery", value: "June 5, 2026", mono: true },
    { label: "Ship-to", value: "Northlake Health · Central sterile services · Chicago, IL" },
    { label: "Status", value: "Open · at risk" },
  ],
  lineItems: [
    {
      line: "10",
      partNumber: "MSS-LP-D-240",
      description: "Locking plate assembly · rev D",
      qty: "240 ea",
      unitPrice: "$3,500.00",
      promised: "June 5, 2026",
    },
  ],
  contract: {
    clause: "§7.2",
    heading: "Late-delivery penalty",
    body:
      "If Seller fails to deliver conforming Goods on the Committed Delivery Date, Seller shall pay Purchaser liquidated damages equal to 1.5% of the affected line-item value per week of delay, up to a maximum of 8% of the affected line-item value. Weeks are counted from the Committed Delivery Date, rounded up.",
    highlightedTerm: "1.5% of the affected line-item value per week",
  },
  deliverySchedule: [
    {
      slot: "Primary",
      date: "June 5, 2026",
      qty: "240 ea",
      status: "At risk · dependency slipping",
    },
  ],
};

export const records = {
  email: emailRecord,
  po: poRecord,
  wo: woRecord,
  so: soRecord,
};
